import './App.css';
import { EntityHeader } from './components/EntityHeader';
import { Tooltip, TooltipBreadcrumb } from './components/Tooltip';
import { ScrollableTabs } from './components/ScrollableTabs';
import { GeneralSidebar } from './components/GeneralSidebar';
import { AppTopHeader } from './components/AppTopHeader';
import { FiltersSelectionTable } from './components/FiltersSelectionTable';
import { PlatformDrawer } from './components/PlatformDrawer';
import { TabLevelActions } from './components/TabLevelActions';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type UIEvent } from 'react';

const APP_TOP_HEADER_HEIGHT = 56;

type WrapperStudyItem = {
  id: string;
  typeLabel: 'fixed-width' | 'variable-width' | 'full-width';
  variantClass: 'study-container-fixed-sm' | 'study-container-fixed-lg' | 'study-container-variable' | 'study-container-full';
};

type WrapperStudyGroup = {
  id: string;
  title: string;
  description?: string;
  items: WrapperStudyItem[];
};

const WRAPPER_LAYOUT_STUDY_GROUPS: WrapperStudyGroup[] = [
  {
    id: 'group-main-filters',
    title: 'Group 1 - Main filters',
    description:
      'Core filter blocks that define the base layout and keep the most frequently used controls visible.',
    items: [
      { id: 'g1-item-01', typeLabel: 'fixed-width', variantClass: 'study-container-fixed-sm' },
      { id: 'g1-item-02', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g1-item-03', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g1-item-04', typeLabel: 'full-width', variantClass: 'study-container-full' },
      { id: 'g1-item-05', typeLabel: 'fixed-width', variantClass: 'study-container-fixed-lg' },
      { id: 'g1-item-06', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g1-item-07', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g1-item-08', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g1-item-09', typeLabel: 'full-width', variantClass: 'study-container-full' },
    ],
  },
  {
    id: 'group-additional-filters',
    title: 'Group 2 - Additional filters',
    items: [
      { id: 'g2-item-01', typeLabel: 'full-width', variantClass: 'study-container-full' },
      { id: 'g2-item-02', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g2-item-03', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g2-item-04', typeLabel: 'fixed-width', variantClass: 'study-container-fixed-sm' },
      { id: 'g2-item-05', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g2-item-06', typeLabel: 'fixed-width', variantClass: 'study-container-fixed-lg' },
      { id: 'g2-item-07', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g2-item-08', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g2-item-09', typeLabel: 'full-width', variantClass: 'study-container-full' },
    ],
  },
  {
    id: 'group-advanced-filters',
    title: 'Group 3 - Advanced filters',
    description:
      'Advanced configuration blocks intended for edge cases and fine-tuning scenarios in the wrapper layout.',
    items: [
      { id: 'g3-item-01', typeLabel: 'fixed-width', variantClass: 'study-container-fixed-lg' },
      { id: 'g3-item-02', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g3-item-03', typeLabel: 'full-width', variantClass: 'study-container-full' },
      { id: 'g3-item-04', typeLabel: 'fixed-width', variantClass: 'study-container-fixed-sm' },
      { id: 'g3-item-05', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g3-item-06', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g3-item-07', typeLabel: 'full-width', variantClass: 'study-container-full' },
      { id: 'g3-item-08', typeLabel: 'variable-width', variantClass: 'study-container-variable' },
      { id: 'g3-item-09', typeLabel: 'fixed-width', variantClass: 'study-container-fixed-sm' },
    ],
  },
];

function App() {
  const [responsiveSidebarMode] = useState<'scroll' | 'expand'>('expand');
  const mainTabs = [
    'General',
    'Affiliates',
    'Segments',
    'Sources/Subsources',
    'Comments',
    'Resources',
    'History log',
    'Macro rules',
    'Antifraud',
    'Automation rules',
  ];

  const secondaryTabs = [
    'Main setup',
    'Custom values',
    'Payouts & Events',
    'Budgets & Caps',
    'Routes',
    'Hypertargeting',
    'Marketplace overrides',
    'Fallback',
    'Additional attibutes',
  ];
  const countries = [
    { code: 'AF', name: 'Afghanistan', flag: '🇦🇫' },
    { code: 'AX', name: 'Aland Islands', flag: '🇦🇽' },
    { code: 'AL', name: 'Albania', flag: '🇦🇱' },
    { code: 'AS', name: 'American Samoa', flag: '🇦🇸' },
    { code: 'DZ', name: 'Algeria', flag: '🇩🇿' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  ];
  const extraCountries = [
    { code: 'CL', name: 'Chile', flag: '🇨🇱' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  ];
  const allCountries = [...countries, ...extraCountries];
  const countriesTooltip = allCountries.map((country) => `${country.flag} ${country.name}`).join(', ');

  const [activeMainTab, setActiveMainTab] = useState(mainTabs[0]);
  const [activeSecondaryTab, setActiveSecondaryTab] = useState(secondaryTabs[0]);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const appMainContentRef = useRef<HTMLDivElement>(null);
  const [isTabFilterOpen, setIsTabFilterOpen] = useState(false);
  const tabFilterPanelRef = useRef<HTMLDivElement>(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isGeneralSidebarCollapsed, setIsGeneralSidebarCollapsed] = useState(false);
  const [isLayoutSettingsOpen, setIsLayoutSettingsOpen] = useState(false);
  const [isManageLayoutOpen, setIsManageLayoutOpen] = useState(false);
  const [isFilterEnabled, setIsFilterEnabled] = useState(true);
  const [isSummaryEnabled, setIsSummaryEnabled] = useState(true);
  const [isMainTabsEnabled, setIsMainTabsEnabled] = useState(true);
  const [isSecondaryTabsEnabled, setIsSecondaryTabsEnabled] = useState(true);
  const [isHeaderLevelActionsEnabled, setIsHeaderLevelActionsEnabled] = useState(true);
  const [isTitleBackEnabled, setIsTitleBackEnabled] = useState(false);
  const [isTitleStatusEnabled, setIsTitleStatusEnabled] = useState(false);
  const [isTitleInfoEnabled, setIsTitleInfoEnabled] = useState(false);
  const [isTitleInfoExtrasEnabled, setIsTitleInfoExtrasEnabled] = useState(false);
  const [titleInfoMode, setTitleInfoMode] = useState<'results' | 'entity-id'>('results');
  const [titleStatusTone, setTitleStatusTone] = useState<
    'success' | 'warning' | 'danger' | 'neutral' | 'info'
  >('success');
  const [isCollapsableHeadersEnabled, setIsCollapsableHeadersEnabled] = useState(false);
  const [isGroupDescriptionsEnabled, setIsGroupDescriptionsEnabled] = useState(false);
  const [wrapperGroupVisibility, setWrapperGroupVisibility] = useState<Record<string, boolean>>(
    () => Object.fromEntries(WRAPPER_LAYOUT_STUDY_GROUPS.map((group) => [group.id, true]))
  );
  const [wrapperGroupCollapsed, setWrapperGroupCollapsed] = useState<Record<string, boolean>>(
    () => Object.fromEntries(WRAPPER_LAYOUT_STUDY_GROUPS.map((group) => [group.id, false]))
  );
  const [wrapperItemVisibility, setWrapperItemVisibility] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        WRAPPER_LAYOUT_STUDY_GROUPS.flatMap((group) => group.items.map((item) => [item.id, true]))
      )
  );
  const [contentMode, setContentMode] = useState<
    'skeleton' | 'offer-form' | 'wrapper-layout-study' | 'tab-level-actions'
  >('skeleton');
  const [screenDemoMode, setScreenDemoMode] = useState<'manual' | 'statistics' | 'offer'>('manual');
  const [appTopHeaderHiddenOffset, setAppTopHeaderHiddenOffset] = useState(0);
  const summaryScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollSummaryLeft, setCanScrollSummaryLeft] = useState(false);
  const [canScrollSummaryRight, setCanScrollSummaryRight] = useState(false);
  const mainTabsRef = useRef<HTMLDivElement>(null);
  const [mainTabsStickyOffset, setMainTabsStickyOffset] = useState(0);

  useEffect(() => {
    const node = summaryScrollRef.current;
    if (!node) return;

    const updateScrollState = () => {
      setCanScrollSummaryLeft(node.scrollLeft > 2);
      setCanScrollSummaryRight(node.scrollLeft + node.clientWidth < node.scrollWidth - 2);
    };

    updateScrollState();
    node.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(node);

    return () => {
      node.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (!isMainTabsEnabled) {
      setMainTabsStickyOffset(0);
      return;
    }

    const node = mainTabsRef.current;
    if (!node) return;

    const updateOffset = () => {
      setMainTabsStickyOffset(node.offsetHeight);
    };

    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(node);
    window.addEventListener('resize', updateOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOffset);
    };
  }, [isMainTabsEnabled]);

  const scrollSummary = (direction: 'left' | 'right') => {
    const node = summaryScrollRef.current;
    if (!node) return;
    const distance = Math.max(140, node.clientWidth * 0.45);
    node.scrollBy({ left: direction === 'left' ? -distance : distance, behavior: 'smooth' });
  };

  const collapsibleActions = [
    { id: 'refresh', label: 'Refresh', icon: 'refresh' },
    { id: 'export-csv', label: 'Export CSV', icon: 'download' },
    { id: 'share', label: 'Share', icon: 'share' },
    {
      id: 'event',
      label: 'Event timestamp',
      icon: 'nest_clock_farsight_analog',
      options: [
        { id: 'event-timestamp', label: 'Event timestamp' },
        { id: 'visit-timestamp', label: 'Visit timestamp' },
      ],
    },
    { id: 'reports', label: 'Saved reports', icon: 'open_in_new' },
    {
      id: 'manage-layout',
      label: 'Manage layout',
      icon: 'visibility',
      alwaysInOverflow: true,
      onClick: () => setIsManageLayoutOpen(true),
    },
    {
      id: 'chart',
      label: 'Hide chart',
      icon: 'chart_data',
      toggleLabels: ['Hide chart', 'Show chart'] as const,
    },
    { id: 'metrics', label: 'Metrycs', icon: 'table' },
  ];

  const tabLevelFixedActions = [
    { id: 'add-field', label: 'Add field', icon: 'add', variant: 'primary' as const },
  ];

  const tabLevelCollapsibleActions = [
    { id: 'duplicate', label: 'Duplicate', icon: 'content_copy' },
    { id: 'import', label: 'Import', icon: 'upload' },
    { id: 'export', label: 'Export', icon: 'download' },
    {
      id: 'view-mode',
      label: 'Table view',
      icon: 'table_rows',
      options: [
        { id: 'table-view', label: 'Table view' },
        { id: 'card-view', label: 'Card view' },
      ],
    },
    { id: 'reorder', label: 'Reorder fields', icon: 'swap_vert' },
    {
      id: 'visibility',
      label: 'Hide empty',
      icon: 'visibility_off',
      toggleLabels: ['Hide empty', 'Show empty'] as const,
    },
    {
      id: 'advanced',
      label: 'Advanced settings',
      icon: 'tune',
      alwaysInOverflow: true,
    },
    { id: 'history', label: 'Field history', icon: 'history' },
  ];

  const useSidebarArrows = responsiveSidebarMode === 'scroll';
  const placeholderGroups = Array.from({ length: 12 }, (_, index) => index);
  const handleEntityContentScroll = (event: UIEvent<HTMLDivElement>) => {
    const nextOffset = Math.min(event.currentTarget.scrollTop, APP_TOP_HEADER_HEIGHT);
    setAppTopHeaderHiddenOffset(nextOffset);
  };
  const appMainAreaStyle = {
    '--app-top-header-hidden-offset': `${appTopHeaderHiddenOffset}px`,
  } as CSSProperties;

  useEffect(() => {
    if (!isFilterEnabled && isFilterOpen) {
      setIsFilterOpen(false);
    }
  }, [isFilterEnabled, isFilterOpen]);

  useEffect(() => {
    if (!isFilterEnabled && isTabFilterOpen) {
      setIsTabFilterOpen(false);
    }
  }, [isFilterEnabled, isTabFilterOpen]);

  useEffect(() => {
    if (!isHeaderLevelActionsEnabled && isFilterOpen) {
      setIsFilterOpen(false);
    }
  }, [isHeaderLevelActionsEnabled, isFilterOpen]);

  const handleHeaderFilterOpenChange = (open: boolean) => {
    setIsFilterOpen(open);
    if (open) {
      appMainContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleWrapperGroupVisibility = (groupId: string) => {
    setWrapperGroupVisibility((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const toggleWrapperItemVisibility = (itemId: string) => {
    setWrapperItemVisibility((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const toggleWrapperGroupCollapsed = (groupId: string) => {
    if (!isCollapsableHeadersEnabled) return;
    setWrapperGroupCollapsed((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const selectManualScreenDemo = () => {
    setScreenDemoMode('manual');
  };

  const applyScreenDemoPreset = (mode: 'manual' | 'statistics' | 'offer') => {
    setScreenDemoMode(mode);

    if (mode === 'statistics') {
      setIsFilterEnabled(true);
      setIsSummaryEnabled(false);
      setIsMainTabsEnabled(false);
      setIsSecondaryTabsEnabled(false);
      setIsHeaderLevelActionsEnabled(true);
      setContentMode((prev) => (prev === 'tab-level-actions' ? 'skeleton' : prev));
      return;
    }

    if (mode === 'offer') {
      setIsHeaderLevelActionsEnabled(false);
      setIsSummaryEnabled(true);
      setIsMainTabsEnabled(true);
      setIsSecondaryTabsEnabled(true);
      setContentMode('tab-level-actions');
    }
  };

  const entityHeaderTitle = screenDemoMode === 'offer' ? 'Offer name' : 'Statistics';
  const entityHeaderInfoText =
    titleInfoMode === 'entity-id' ? '#3456' : '18,789 results';
  const entityHeaderInfoExtras = (
    <>
      <span className="eh-title-chip">
        <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14 }}>
          public
        </span>
        Public
      </span>
      <span className="eh-title-chip eh-title-chip-muted">CPE</span>
    </>
  );
  const entityHeaderTitleTooltip =
    screenDemoMode === 'offer' ? (
      <TooltipBreadcrumb
        items={[
          {
            id: 'advertisers',
            label: 'Advertisers',
            onClick: () => console.info('Navigate to Advertisers'),
          },
          {
            id: 'advertiser',
            label: 'Acme Media',
            onClick: () => console.info('Navigate to Advertiser'),
          },
          { id: 'offer', label: 'Offer name' },
        ]}
      />
    ) : undefined;

  return (
    <main className="app-shell">
      <div className="app-nav-layout">
        <div
          className={`general-sidebar-layer ${
            isMobileSidebarOpen ? 'general-sidebar-layer-open' : ''
          } ${isGeneralSidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
        >
          <button
            type="button"
            className="general-sidebar-overlay"
            aria-label="Cerrar menú"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          <GeneralSidebar
            isCollapsed={isGeneralSidebarCollapsed}
            onToggleCollapsed={() => setIsGeneralSidebarCollapsed((prev) => !prev)}
          />
        </div>

        <div className="app-main-area" style={appMainAreaStyle}>
          <AppTopHeader
            onMobileMenuClick={() => setIsMobileSidebarOpen(true)}
            platformName="Aftrad"
            hiddenOffset={appTopHeaderHiddenOffset}
          />
          <EntityHeader
            title={entityHeaderTitle}
            titleTooltip={entityHeaderTitleTooltip}
            showBack={isTitleBackEnabled}
            onBack={() => window.history.back()}
            showStatus={isTitleStatusEnabled}
            statusTone={titleStatusTone}
            statusLabel={`Status: ${titleStatusTone}`}
            showInfo={isTitleInfoEnabled}
            infoText={entityHeaderInfoText}
            showInfoExtras={isTitleInfoExtrasEnabled}
            infoExtras={entityHeaderInfoExtras}
            collapsibleActions={collapsibleActions}
            isFilterOpen={isFilterEnabled ? isFilterOpen : false}
            onFilterOpenChange={handleHeaderFilterOpenChange}
            filterPanelRef={filterPanelRef}
            showFilterAction={isFilterEnabled}
            showActions={isHeaderLevelActionsEnabled}
          />

          <div
            className="app-main-content"
            ref={appMainContentRef}
            onScroll={handleEntityContentScroll}
            style={
              {
                '--main-tabs-sticky-offset': `${mainTabsStickyOffset}px`,
              } as CSSProperties
            }
          >
            {isFilterEnabled && isFilterOpen && (
              <div className="entity-filters-panel" ref={filterPanelRef}>
                <FiltersSelectionTable />
              </div>
            )}

            <section className={`entity-layout ${!isSummaryEnabled ? 'entity-layout-no-summary' : ''}`}>
              <div className="entity-main-column">
                {isMainTabsEnabled && (
                  <div className="main-tabs-sticky" ref={mainTabsRef}>
                    <ScrollableTabs
                      className="main-tabs"
                      tabs={mainTabs}
                      activeTab={activeMainTab}
                      onTabChange={setActiveMainTab}
                    />
                  </div>
                )}

                <div className="entity-content-panel">
                  {activeMainTab === 'General' && isSecondaryTabsEnabled && (
                    <div className="secondary-tabs-sticky">
                      <ScrollableTabs
                        className="secondary-tabs"
                        tabs={secondaryTabs}
                        activeTab={activeSecondaryTab}
                        onTabChange={setActiveSecondaryTab}
                        variant="pill"
                      />
                    </div>
                  )}
                  {contentMode === 'tab-level-actions' && (
                    <div className="tab-level-actions-content">
                      <TabLevelActions
                        fixedActions={tabLevelFixedActions}
                        collapsibleActions={tabLevelCollapsibleActions}
                        isFilterOpen={isFilterEnabled ? isTabFilterOpen : false}
                        onFilterOpenChange={setIsTabFilterOpen}
                        filterPanelRef={tabFilterPanelRef}
                        showFilterAction={isFilterEnabled}
                      />
                    </div>
                  )}
                  {contentMode === 'tab-level-actions' && isFilterEnabled && isTabFilterOpen && (
                    <div className="entity-filters-panel tla-filters-panel" ref={tabFilterPanelRef}>
                      <FiltersSelectionTable />
                    </div>
                  )}
                <div className="entity-wrapper-content">
                  {contentMode === 'skeleton' ? (
                    <div className="placeholder-form">
                      {placeholderGroups.map((group) => (
                        <section key={group} className="placeholder-form-group">
                          <div className="placeholder-label" />
                          <div className="placeholder-row">
                            <div className="placeholder-input placeholder-input-short" />
                            <div className="placeholder-input" />
                          </div>
                          <div className="placeholder-input placeholder-input-full" />
                        </section>
                      ))}
                    </div>
                  ) : contentMode === 'offer-form' ? (
                    <div className="offer-form-placeholder">
                      <h4>Offer form</h4>
                      <p>Espacio reservado para inyectar el contenido de Offer form.</p>
                    </div>
                  ) : contentMode === 'tab-level-actions' ? (
                    <div className="placeholder-form tab-level-actions-placeholder">
                      {placeholderGroups.slice(0, 6).map((group) => (
                        <section key={group} className="placeholder-form-group">
                          <div className="placeholder-label" />
                          <div className="placeholder-row">
                            <div className="placeholder-input placeholder-input-short" />
                            <div className="placeholder-input" />
                          </div>
                          <div className="placeholder-input placeholder-input-full" />
                        </section>
                      ))}
                    </div>
                  ) : (
                    <div className="wrapper-layout-study">
                      {WRAPPER_LAYOUT_STUDY_GROUPS.map((group) =>
                        wrapperGroupVisibility[group.id] ? (
                          <div key={group.id} className="wrapper-layout-study-group">
                            <button
                              type="button"
                              className={`wrapper-layout-study-heading-btn ${
                                isCollapsableHeadersEnabled ? 'is-collapsable' : ''
                              }`}
                              onClick={() => toggleWrapperGroupCollapsed(group.id)}
                              aria-expanded={!wrapperGroupCollapsed[group.id]}
                            >
                              <h4 className="wrapper-layout-study-heading">{group.title}</h4>
                              {isCollapsableHeadersEnabled && (
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  {wrapperGroupCollapsed[group.id] ? 'expand_more' : 'expand_less'}
                                </span>
                              )}
                            </button>
                            {!wrapperGroupCollapsed[group.id] && (
                              <>
                                {isGroupDescriptionsEnabled && group.description && (
                                  <p className="wrapper-layout-study-description">{group.description}</p>
                                )}
                                <div className="wrapper-layout-study-grid">
                                  {group.items
                                    .filter((item) => wrapperItemVisibility[item.id])
                                    .map((item) => (
                                      <div
                                        key={item.id}
                                        className={`study-container ${item.variantClass}`}
                                        data-wrapper-item-id={item.id}
                                      >
                                        <span>{item.typeLabel}</span>
                                        <span className="study-container-id">{item.id}</span>
                                      </div>
                                    ))}
                                </div>
                              </>
                            )}
                          </div>
                        ) : null
                      )}
                    </div>
                  )}
                </div>
                </div>
              </div>

          {isSummaryEnabled && (
            <aside
              className={`entity-sidebar ${
                responsiveSidebarMode === 'scroll'
                  ? 'entity-sidebar-responsive-scroll'
                  : 'entity-sidebar-responsive-expand'
              }`}
            >
              {useSidebarArrows && canScrollSummaryLeft && (
                <button
                  type="button"
                  className="summary-scroll-arrow summary-scroll-arrow-left"
                  onClick={() => scrollSummary('left')}
                  aria-label="Desplazar summary a la izquierda"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    chevron_left
                  </span>
                </button>
              )}
              <div
                className={`entity-sidebar-scroll ${
                  responsiveSidebarMode === 'expand' ? 'entity-sidebar-scroll-expand' : ''
                }`}
                ref={summaryScrollRef}
              >
                <h2>Summary</h2>
                <div className="sidebar-item">
                  <span>Conversion model</span>
                  <strong
                    className="sidebar-chip sidebar-chip-conversion sidebar-tooltip-target"
                    data-tooltip="Conversion model"
                  >
                    CPE
                  </strong>
                </div>
                <div className="sidebar-item">
                  <span>Visibility</span>
                  <strong
                    className="sidebar-chip sidebar-chip-visibility sidebar-tooltip-target"
                    data-tooltip="Visibility"
                  >
                    <span className="material-icons sidebar-chip-icon-filled" aria-hidden="true">
                      visibility
                    </span>
                    Public
                  </strong>
                </div>
                <div className="sidebar-item">
                  <span>Traffic status</span>
                  <strong
                    className="sidebar-chip sidebar-chip-traffic sidebar-tooltip-target"
                    data-tooltip="Traffic status"
                  >
                    <span className="material-icons sidebar-chip-icon-filled" aria-hidden="true">
                      check_circle
                    </span>
                    Accepts traffic
                  </strong>
                </div>
                <div className="sidebar-item">
                  <span>Advertiser</span>
                  <a
                    className="sidebar-value-link sidebar-tooltip-target"
                    href="#advertiser"
                    data-tooltip="Advertiser"
                  >
                    223 - Jorge Rubia
                  </a>
                </div>
                <div className="sidebar-item">
                  <span>Timezone</span>
                  <strong className="sidebar-tooltip-target" data-tooltip="Timezone">
                    UTC+8
                  </strong>
                </div>
                <div className="sidebar-item">
                  <span>Boost</span>
                  <strong
                    className="sidebar-chip sidebar-chip-boost sidebar-tooltip-target"
                    data-tooltip="Boost"
                  >
                    <span className="material-icons sidebar-chip-icon-filled" aria-hidden="true">
                      bolt
                    </span>
                    Boosted
                  </strong>
                </div>
                <div className="sidebar-item">
                  <span>Account manager</span>
                  <a
                    className="sidebar-value-link sidebar-account-manager-link sidebar-tooltip-target"
                    href="#account-manager"
                    data-tooltip="Account manager"
                  >
                    Michal Kocztorz
                  </a>
                  <a
                    className="sidebar-account-manager-badge sidebar-tooltip-target"
                    href="#account-manager"
                    data-tooltip="Account manager"
                  >
                    MK
                  </a>
                </div>
                <div className="sidebar-item">
                  <span>Platforms</span>
                  <strong>CTV, Mobile, Tablet</strong>
                </div>
                <div className="sidebar-item sidebar-item-countries">
                  <span>Countries</span>
                  <div className="sidebar-country-list sidebar-tooltip-target" data-tooltip="Countries">
                    {[...countries, ...(showAllCountries ? extraCountries : [])].map((country) => (
                      <span key={country.code} className="sidebar-country-pill">
                        <span className="sidebar-country-flag" aria-hidden="true">
                          {country.flag}
                        </span>
                        {country.name}
                      </span>
                    ))}
                    {!showAllCountries && (
                      <button
                        type="button"
                        className="sidebar-country-more"
                        onClick={() => setShowAllCountries(true)}
                      >
                        +13
                      </button>
                    )}
                  </div>
                  <div
                    className="sidebar-country-list-compact sidebar-tooltip-target"
                    data-tooltip={`Countries: ${countriesTooltip}`}
                  >
                    {allCountries.slice(0, 3).map((country) => (
                      <span key={`${country.code}-compact`} className="sidebar-country-pill">
                        <span className="sidebar-country-flag" aria-hidden="true">
                          {country.flag}
                        </span>
                        {country.name}
                      </span>
                    ))}
                    <span className="sidebar-country-more-static">+{allCountries.length}</span>
                  </div>
                </div>
              </div>
              {useSidebarArrows && canScrollSummaryRight && (
                <button
                  type="button"
                  className="summary-scroll-arrow summary-scroll-arrow-right"
                  onClick={() => scrollSummary('right')}
                  aria-label="Desplazar summary a la derecha"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    chevron_right
                  </span>
                </button>
              )}
            </aside>
          )}
            </section>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="layout-settings-fab"
        aria-label="Abrir ajustes de layout"
        onClick={() => setIsLayoutSettingsOpen(true)}
      >
        <span className="material-icons" aria-hidden="true">
          settings
        </span>
      </button>

      <div
        className={`layout-settings-layer ${isLayoutSettingsOpen ? 'layout-settings-layer-open' : ''}`}
        role="presentation"
      >
        <button
          type="button"
          className="layout-settings-overlay"
          aria-label="Cerrar ajustes de layout"
          onClick={() => setIsLayoutSettingsOpen(false)}
        />
        <aside className="layout-settings-sidebar" aria-label="Ajustes de layout">
          <div className="layout-settings-header">
            <h3>Layout settings</h3>
            <button
              type="button"
              className="layout-settings-close"
              aria-label="Cerrar panel"
              onClick={() => setIsLayoutSettingsOpen(false)}
            >
              <span className="material-icons" aria-hidden="true">
                close
              </span>
            </button>
          </div>
          <div className="layout-settings-content">
            <div className="layout-setting-section-title">Screen demo</div>
            <div className="layout-setting-radio-group" role="radiogroup" aria-label="Screen demo">
              <div className="layout-setting-item">
                <div className="layout-setting-label">Manual</div>
                <button
                  type="button"
                  role="radio"
                  aria-checked={screenDemoMode === 'manual'}
                  className={`layout-setting-toggle ${screenDemoMode === 'manual' ? 'is-on' : ''}`}
                  onClick={() => applyScreenDemoPreset('manual')}
                >
                  <span className="layout-setting-toggle-thumb" />
                </button>
              </div>
              <div className="layout-setting-item">
                <div className="layout-setting-label">Statistics</div>
                <button
                  type="button"
                  role="radio"
                  aria-checked={screenDemoMode === 'statistics'}
                  className={`layout-setting-toggle ${screenDemoMode === 'statistics' ? 'is-on' : ''}`}
                  onClick={() => applyScreenDemoPreset('statistics')}
                >
                  <span className="layout-setting-toggle-thumb" />
                </button>
              </div>
              <div className="layout-setting-item">
                <div className="layout-setting-label">Offer</div>
                <button
                  type="button"
                  role="radio"
                  aria-checked={screenDemoMode === 'offer'}
                  className={`layout-setting-toggle ${screenDemoMode === 'offer' ? 'is-on' : ''}`}
                  onClick={() => applyScreenDemoPreset('offer')}
                >
                  <span className="layout-setting-toggle-thumb" />
                </button>
              </div>
            </div>
            <div className="layout-setting-divider" role="presentation" />
            <div className="layout-setting-item">
              <div className="layout-setting-label">Filter</div>
              <button
                type="button"
                role="switch"
                aria-checked={isFilterEnabled}
                className={`layout-setting-toggle ${isFilterEnabled ? 'is-on' : ''}`}
                onClick={() => {
                  selectManualScreenDemo();
                  setIsFilterEnabled((prev) => !prev);
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            <div className="layout-setting-item">
              <div className="layout-setting-label">Summary</div>
              <button
                type="button"
                role="switch"
                aria-checked={isSummaryEnabled}
                className={`layout-setting-toggle ${isSummaryEnabled ? 'is-on' : ''}`}
                onClick={() => {
                  selectManualScreenDemo();
                  setIsSummaryEnabled((prev) => !prev);
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            <div className="layout-setting-item">
              <div className="layout-setting-label">Main tabs</div>
              <button
                type="button"
                role="switch"
                aria-checked={isMainTabsEnabled}
                className={`layout-setting-toggle ${isMainTabsEnabled ? 'is-on' : ''}`}
                onClick={() => {
                  selectManualScreenDemo();
                  setIsMainTabsEnabled((prev) => !prev);
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            <div className="layout-setting-item">
              <div className="layout-setting-label">Secondary tabs</div>
              <button
                type="button"
                role="switch"
                aria-checked={isSecondaryTabsEnabled}
                className={`layout-setting-toggle ${isSecondaryTabsEnabled ? 'is-on' : ''}`}
                onClick={() => {
                  selectManualScreenDemo();
                  setIsSecondaryTabsEnabled((prev) => !prev);
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            <div className="layout-setting-item">
              <div className="layout-setting-label">Tab level actions</div>
              <button
                type="button"
                role="switch"
                aria-checked={contentMode === 'tab-level-actions'}
                className={`layout-setting-toggle ${
                  contentMode === 'tab-level-actions' ? 'is-on' : ''
                }`}
                onClick={() => {
                  selectManualScreenDemo();
                  setContentMode((prev) =>
                    prev === 'tab-level-actions' ? 'skeleton' : 'tab-level-actions'
                  );
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            <div className="layout-setting-item">
              <div className="layout-setting-label">Header level actions</div>
              <button
                type="button"
                role="switch"
                aria-checked={isHeaderLevelActionsEnabled}
                className={`layout-setting-toggle ${isHeaderLevelActionsEnabled ? 'is-on' : ''}`}
                onClick={() => {
                  selectManualScreenDemo();
                  setIsHeaderLevelActionsEnabled((prev) => !prev);
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            <div className="layout-setting-divider" role="presentation" />
            <div className="layout-setting-section-title layout-setting-section-title-with-tooltip">
              <Tooltip
                trigger="icon"
                placement="top"
                content="Controls the pieces of the entity title: back, status, info and extras."
                iconLabel="About Entity title"
              >
                Entity title
              </Tooltip>
            </div>
            <div className="layout-setting-item">
              <div className="layout-setting-label">Back</div>
              <button
                type="button"
                role="switch"
                aria-checked={isTitleBackEnabled}
                className={`layout-setting-toggle ${isTitleBackEnabled ? 'is-on' : ''}`}
                onClick={() => {
                  selectManualScreenDemo();
                  setIsTitleBackEnabled((prev) => !prev);
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            <div className="layout-setting-item">
              <div className="layout-setting-label">Status</div>
              <button
                type="button"
                role="switch"
                aria-checked={isTitleStatusEnabled}
                className={`layout-setting-toggle ${isTitleStatusEnabled ? 'is-on' : ''}`}
                onClick={() => {
                  selectManualScreenDemo();
                  setIsTitleStatusEnabled((prev) => !prev);
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            {isTitleStatusEnabled && (
              <div className="layout-setting-radio-group" role="radiogroup" aria-label="Status tone">
                {(
                  [
                    ['success', 'Success'],
                    ['warning', 'Warning'],
                    ['danger', 'Danger'],
                    ['info', 'Info'],
                    ['neutral', 'Neutral'],
                  ] as const
                ).map(([tone, label]) => (
                  <div key={tone} className="layout-setting-item layout-setting-item-nested">
                    <div className="layout-setting-label">{label}</div>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={titleStatusTone === tone}
                      className={`layout-setting-toggle ${
                        titleStatusTone === tone ? 'is-on' : ''
                      }`}
                      onClick={() => {
                        selectManualScreenDemo();
                        setTitleStatusTone(tone);
                      }}
                    >
                      <span className="layout-setting-toggle-thumb" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="layout-setting-item">
              <div className="layout-setting-label">Info</div>
              <button
                type="button"
                role="switch"
                aria-checked={isTitleInfoEnabled}
                className={`layout-setting-toggle ${isTitleInfoEnabled ? 'is-on' : ''}`}
                onClick={() => {
                  selectManualScreenDemo();
                  setIsTitleInfoEnabled((prev) => {
                    if (prev) {
                      setIsTitleInfoExtrasEnabled(false);
                    }
                    return !prev;
                  });
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            {isTitleInfoEnabled && (
              <div className="layout-setting-radio-group" role="radiogroup" aria-label="Info type">
                <div className="layout-setting-item layout-setting-item-nested">
                  <div className="layout-setting-label">Results count</div>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={titleInfoMode === 'results'}
                    className={`layout-setting-toggle ${titleInfoMode === 'results' ? 'is-on' : ''}`}
                    onClick={() => {
                      selectManualScreenDemo();
                      setTitleInfoMode('results');
                    }}
                  >
                    <span className="layout-setting-toggle-thumb" />
                  </button>
                </div>
                <div className="layout-setting-item layout-setting-item-nested">
                  <div className="layout-setting-label">Entity ID</div>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={titleInfoMode === 'entity-id'}
                    className={`layout-setting-toggle ${
                      titleInfoMode === 'entity-id' ? 'is-on' : ''
                    }`}
                    onClick={() => {
                      selectManualScreenDemo();
                      setTitleInfoMode('entity-id');
                    }}
                  >
                    <span className="layout-setting-toggle-thumb" />
                  </button>
                </div>
              </div>
            )}
            <div className="layout-setting-item">
              <div className="layout-setting-label">Info extras</div>
              <button
                type="button"
                role="switch"
                aria-checked={isTitleInfoExtrasEnabled}
                className={`layout-setting-toggle ${isTitleInfoExtrasEnabled ? 'is-on' : ''}`}
                disabled={!isTitleInfoEnabled}
                onClick={() => {
                  if (!isTitleInfoEnabled) return;
                  selectManualScreenDemo();
                  setIsTitleInfoExtrasEnabled((prev) => !prev);
                }}
              >
                <span className="layout-setting-toggle-thumb" />
              </button>
            </div>
            <div className="layout-setting-divider" role="presentation" />
            <div className="layout-setting-section-title">Content</div>
            <div className="layout-setting-radio-group" role="radiogroup" aria-label="Content">
              <div className="layout-setting-item">
                <div className="layout-setting-label">Skeleton content</div>
                <button
                  type="button"
                  role="radio"
                  aria-checked={contentMode === 'skeleton'}
                  className={`layout-setting-toggle ${contentMode === 'skeleton' ? 'is-on' : ''}`}
                  onClick={() => {
                    selectManualScreenDemo();
                    setContentMode('skeleton');
                  }}
                >
                  <span className="layout-setting-toggle-thumb" />
                </button>
              </div>
              <div className="layout-setting-item">
                <div className="layout-setting-label">Offer form</div>
                <button
                  type="button"
                  role="radio"
                  aria-checked={contentMode === 'offer-form'}
                  className={`layout-setting-toggle ${contentMode === 'offer-form' ? 'is-on' : ''}`}
                  onClick={() => {
                    selectManualScreenDemo();
                    setContentMode('offer-form');
                  }}
                >
                  <span className="layout-setting-toggle-thumb" />
                </button>
              </div>
              <div className="layout-setting-item">
                <div className="layout-setting-label">Wrapper layout study</div>
                <button
                  type="button"
                  role="radio"
                  aria-checked={contentMode === 'wrapper-layout-study'}
                  className={`layout-setting-toggle ${
                    contentMode === 'wrapper-layout-study' ? 'is-on' : ''
                  }`}
                  onClick={() => {
                    selectManualScreenDemo();
                    setContentMode('wrapper-layout-study');
                  }}
                >
                  <span className="layout-setting-toggle-thumb" />
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <PlatformDrawer
        isOpen={isManageLayoutOpen}
        title="Manage layout"
        onClose={() => setIsManageLayoutOpen(false)}
        onApply={() => setIsManageLayoutOpen(false)}
        onCancel={() => setIsManageLayoutOpen(false)}
      >
        <div className="manage-layout-panel">
          <div className="layout-setting-item">
            <div className="layout-setting-label">Collapsable headers</div>
            <button
              type="button"
              role="switch"
              aria-checked={isCollapsableHeadersEnabled}
              className={`layout-setting-toggle ${isCollapsableHeadersEnabled ? 'is-on' : ''}`}
              onClick={() => setIsCollapsableHeadersEnabled((prev) => !prev)}
            >
              <span className="layout-setting-toggle-thumb" />
            </button>
          </div>
          <div className="layout-setting-item">
            <div className="layout-setting-label">Group descriptions</div>
            <button
              type="button"
              role="switch"
              aria-checked={isGroupDescriptionsEnabled}
              className={`layout-setting-toggle ${isGroupDescriptionsEnabled ? 'is-on' : ''}`}
              onClick={() => setIsGroupDescriptionsEnabled((prev) => !prev)}
            >
              <span className="layout-setting-toggle-thumb" />
            </button>
          </div>
          <div className="layout-setting-divider" role="presentation" />
          <div className="layout-setting-section-title">Wrapper layout study</div>
          {WRAPPER_LAYOUT_STUDY_GROUPS.map((group) => (
            <div key={group.id} className="manage-layout-group">
              <div className="layout-setting-item">
                <div className="layout-setting-label">{group.title}</div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={wrapperGroupVisibility[group.id]}
                  className={`layout-setting-toggle ${wrapperGroupVisibility[group.id] ? 'is-on' : ''}`}
                  onClick={() => toggleWrapperGroupVisibility(group.id)}
                >
                  <span className="layout-setting-toggle-thumb" />
                </button>
              </div>

              <div className="manage-layout-item-list">
                {group.items.map((item) => (
                  <div key={item.id} className="layout-setting-item manage-layout-item">
                    <div className="manage-layout-item-labels">
                      <div className="layout-setting-label">{item.typeLabel}</div>
                      <div className="manage-layout-item-id">{item.id}</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={wrapperItemVisibility[item.id]}
                      className={`layout-setting-toggle ${wrapperItemVisibility[item.id] ? 'is-on' : ''}`}
                      onClick={() => toggleWrapperItemVisibility(item.id)}
                    >
                      <span className="layout-setting-toggle-thumb" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PlatformDrawer>
    </main>
  );
}

export default App;
